import type {CreateExtArgs, CreateExtensionArgument, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {Pointer, CompRef, OverrideOrDefaultData, BuilderPresets, BuilderStyle} from '@wix/document-services-types'
import type {
    Archetype,
    CssPropertyItem,
    CssCustomPropertyItem,
    PresetInnerElementDefaults,
    CssPropertyEnumCssPropertyType
} from '@wix/ambassador-devcenter-v1-component-type-data/types'
import type {PlatformExtensionAPI} from '../platform'
import type {StructureExtensionAPI} from '../structure'
import type {InnerElementsExtensionAPI} from './innerElements'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {PresetsExtensionAPI} from '../presets'
import {DATA_TYPES} from '../../constants/constants'
import type {
    CssCustomProperties,
    CssProperties,
    EditorReactComponent,
    ElementItem
} from '@wix/component-protocol/schema'
import _ from 'lodash'

interface ManifestApi extends ExtensionAPI {
    getArchType(componentType: string): Archetype
    getDisplayName(componentType: string): string
    getData(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    getStyle(
        compPointer: Pointer,
        withInnerElements: boolean,
        types?: {prop: string; extract?(data: any): any}[]
    ): OverrideOrDefaultData[]
    innerElements: {
        buildTraversableFlatMap(compPointer: CompRef): Record<string, {innerElements: string[]}>
    }
    cssStyle: {
        getDefaultDefinition(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>>
        getCustomCssPropertiesRoles(
            compPointer: CompRef,
            innerElementsPath?: string[]
        ): Readonly<Record<string, CssPropertyEnumCssPropertyType>>
    }
}

interface ManifestExtensionAPI extends ExtensionAPI {
    manifest: ManifestApi
}

interface ManifestMetaData {
    dataType: 'data' | 'cssProperties' | 'cssCustomProperties'
    namespace: 'data' | 'style'
}

type ExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined
) => OverrideOrDefaultData[]

const genericExtractDataFunction = (
    override: any,
    manifestData: Record<string, any>,
    typeNameInManifest: string,
    whereToWriteInDM: string,
    presetData: any,
    types: {prop: string; extract?(data: any): any}[] | undefined
) => {
    const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
    Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
        const type = meta[typeNameInManifest]
        const extractionDetails = types?.find(toExtractType => toExtractType.prop === type)
        if (!types || extractionDetails) {
            const defaultValue = presetData?.[typeNameInManifest]?.[prop]?.defaultValue ?? meta?.defaultValue
            const value = override?.[prop] ?? defaultValue
            overrideOrDefaultDataArr.push({
                prop,
                value: extractionDetails?.extract ? extractionDetails.extract(value) : value,
                type,
                whereToWriteInDM,
                metaData: meta[type] ?? {}
            })
        }
    })
    return overrideOrDefaultDataArr
}

const typeToExtractDataFunction: Record<string, ExtractDataFunction> = {
    data: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => genericExtractDataFunction(override, manifestData, 'dataType', 'data', presetData, types),
    cssProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => {
        const whereToWriteInDM = 'cssProperties'
        if (!types) {
            return Object.entries(manifestData).map(([prop, meta]: [string, any]) => {
                const defaultValue = presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? meta?.defaultValue
                const value = override?.[prop] ?? defaultValue
                return {
                    value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
        }
        return types
            .map(typeToExtract => {
                const {prop, extract} = typeToExtract
                const defaultValue =
                    presetData?.[whereToWriteInDM]?.[prop]?.defaultValue ?? manifestData?.[prop]?.defaultValue
                const value = override?.[prop] ?? defaultValue
                const meta = manifestData?.[prop]
                return {
                    value: extract ? extract(value) : value,
                    whereToWriteInDM,
                    prop,
                    type: prop,
                    metaData: meta?.[prop] ?? {}
                }
            })
            .filter(context => context.value !== undefined)
    },
    cssCustomProperties: (
        override: any,
        manifestData: Record<string, any>,
        presetData: any,
        types: {prop: string; extract?(data: any): any}[] | undefined
    ) => genericExtractDataFunction(override, manifestData, 'cssPropertyType', 'cssCustomProperties', presetData, types)
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    return {
        name: 'manifest',
        dependencies: new Set(['platform', 'structure', 'innerElements', 'variants', 'presets']),
        createExtensionAPI(extArgs: CreateExtArgs): ManifestExtensionAPI {
            const {extensionAPI, dal, pointers} = extArgs
            const {platform} = extensionAPI as PlatformExtensionAPI
            const {components} = extensionAPI as StructureExtensionAPI
            const {variants} = extensionAPI as VariantsExtensionAPI

            const getComponentDetailsFromCSM = (componentType: string): any => {
                const builderCompData = platform.getBuilderCompAppData(componentType)
                if (!builderCompData) return undefined
                const {appDefinitionId, componentId} = builderCompData
                const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
                const appData = dal.get(appPointer)
                const {data} = appData.components.find((comp: any) => comp.componentId === componentId)
                return data
            }

            const getOverridesOrDefaultFromType = (
                manifestData: Record<string, any>,
                override: any,
                manifestMetaData: ManifestMetaData,
                presetData: any,
                types: {prop: string; extract?(data: any): any}[] | undefined
            ) => {
                return typeToExtractDataFunction[manifestMetaData.dataType](override, manifestData, presetData, types)
            }

            const getManifestDataElementsAndPresets = (
                compPointer: Pointer,
                manifestMetaData: ManifestMetaData
            ): {manifestData: any; elements: any; compPresets: any} => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType)
                const {[manifestMetaData.dataType]: manifestData, elements, presets: compPresets} = editorElement
                return {manifestData, elements, compPresets}
            }

            const getComponentOverridesOrDefaultFromType = (
                compPointer: Pointer,
                manifestMetaData: ManifestMetaData,
                withInnerElements: boolean = false,
                types?: {prop: string; extract?(data: any): any}[]
            ): OverrideOrDefaultData[] => {
                const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                const {presets} = extensionAPI as PresetsExtensionAPI

                const flattenPresets = (compPresets: any) => {
                    if (!compPresets) return {}
                    return Object.fromEntries(
                        Object.entries(compPresets).map(([name, data]: [string, any]) => [
                            name,
                            data?.elements || data?.presetDefaults?.elements
                        ])
                    )
                }

                const getInnerElementsPreset = (
                    componentPointer: Pointer,
                    path: string[]
                ): BuilderPresets | undefined => {
                    return (
                        innerElements.getInnerElementPresets(componentPointer as CompRef, path) ||
                        presets.get(componentPointer as CompRef)
                    )
                }

                const getOverridesOrDefaultFromInnerElements = (
                    componentPointer: Pointer,
                    elements: Record<string, any>,
                    relativePath: string[],
                    overrideOrDefaultDataArr: OverrideOrDefaultData[],
                    compPresets: any,
                    requestedTypes: {prop: string; extract?(data: any): any}[] | undefined
                ) => {
                    if (!elements) return overrideOrDefaultDataArr
                    Object.entries(elements).forEach(([elem, val]: [string, any]) => {
                        const path = [...relativePath, elem]
                        const resolved = val.inlineElement ?? val.refElement?.resolvedElement ?? val
                        const innerElementData = resolved?.[manifestMetaData.dataType]
                        if (innerElementData) {
                            const override =
                                manifestMetaData.namespace === DATA_TYPES.theme
                                    ? innerElements.getInnerElementStyle(componentPointer as CompRef, path)
                                    : innerElements.getInnerElementData(componentPointer as CompRef, path)
                            const innerElementsOverrideNested = (override as any)?.[manifestMetaData.dataType] ?? {}
                            const currentPreset = getInnerElementsPreset(componentPointer, path)
                            const presetData = currentPreset && compPresets?.[currentPreset.presetName]?.[elem]
                            overrideOrDefaultDataArr.push(
                                ...getOverridesOrDefaultFromType(
                                    innerElementData,
                                    innerElementsOverrideNested,
                                    manifestMetaData,
                                    presetData,
                                    requestedTypes
                                ).map(context => ({...context, path}))
                            )
                        }
                        getOverridesOrDefaultFromInnerElements(
                            componentPointer,
                            resolved?.elements,
                            [...relativePath, elem],
                            overrideOrDefaultDataArr,
                            flattenPresets(compPresets),
                            requestedTypes
                        )
                    })
                    return overrideOrDefaultDataArr
                }
                const {manifestData, elements, compPresets} = getManifestDataElementsAndPresets(
                    compPointer,
                    manifestMetaData
                )
                if (!manifestData)
                    return withInnerElements
                        ? getOverridesOrDefaultFromInnerElements(compPointer, elements, [], [], compPresets, types)
                        : []
                const item = variants.getComponentItemConsideringVariants(compPointer, manifestMetaData.namespace)
                const compOverrides = item?.[manifestMetaData.dataType] ?? {}
                const currentPreset = presets?.get(compPointer as CompRef)
                const presetData = currentPreset && compPresets?.[currentPreset.presetName]?.presetDefaults
                const elementPropsArray = getOverridesOrDefaultFromType(
                    manifestData,
                    compOverrides,
                    manifestMetaData,
                    presetData,
                    types
                )

                return withInnerElements
                    ? [
                          ...elementPropsArray,
                          ...getOverridesOrDefaultFromInnerElements(
                              compPointer,
                              elements,
                              [],
                              [],
                              flattenPresets(compPresets),
                              types
                          )
                      ]
                    : elementPropsArray
            }

            const extractProps = <
                T extends CssProperties | CssCustomProperties,
                K extends keyof (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)
            >(
                obj: T,
                propertyName: K
            ): Readonly<
                Record<string, (T extends CssCustomPropertyItem ? CssCustomPropertyItem : CssPropertyItem)[K]>
            > => {
                return Object.freeze(
                    Object.fromEntries(
                        Object.entries(obj ?? {}).map(([key, value]) => [key, (value as any)[propertyName]])
                    )
                )
            }

            const inflateInnerElementsPath = (
                innerElementsPath: string[] = [],
                prefix = ['elements'],
                suffix = ['inlineElement']
            ): string[] => innerElementsPath.flatMap((element: string) => [...prefix, element, ...suffix])

            const getCustomCssPropertiesRoles = (
                compPointer: CompRef,
                innerElementsPath?: string[]
            ): Readonly<Record<string, CssPropertyEnumCssPropertyType>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const path: string[] = inflateInnerElementsPath(innerElementsPath)

                return extractProps<CssCustomProperties, keyof CssCustomPropertyItem>(
                    _.get(editorElement, [...path, 'cssCustomProperties'], {}) satisfies CssCustomProperties,
                    'cssPropertyType'
                )
            }

            const getDefaultAccordingToAppliedPresets = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const {innerElements} = extensionAPI as InnerElementsExtensionAPI
                const {presets} = extensionAPI as PresetsExtensionAPI
                const defaultsFromPresets = []
                const compPreset = presets.get(compPointer)?.presetName
                const inflatedInnerElementPresetPath = inflateInnerElementsPath(innerElementsPath, ['elements'], [])
                const currentInnerElementPath = []

                if (compPreset) {
                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        ['presets', compPreset, 'presetDefaults', ...inflatedInnerElementPresetPath],
                        {}
                    ) satisfies PresetInnerElementDefaults
                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                for (const innerElement of innerElementsPath) {
                    currentInnerElementPath.push(innerElement)
                    const inflatedInnerElementPath = inflateInnerElementsPath(currentInnerElementPath)
                    inflatedInnerElementPresetPath.splice(0, 2)
                    const elementPreset = innerElements.getInnerElementPresets(
                        compPointer,
                        currentInnerElementPath
                    )?.presetName

                    if (!elementPreset) {
                        continue
                    }

                    const {cssProperties, cssCustomProperties} = _.get(
                        editorElement,
                        [
                            ...inflatedInnerElementPath,
                            'presets',
                            elementPreset,
                            'presetDefaults',
                            ...inflatedInnerElementPresetPath
                        ],
                        {}
                    ) satisfies PresetInnerElementDefaults

                    defaultsFromPresets.push({
                        cssProperties: extractProps(cssProperties, 'defaultValue'),
                        cssCustomProperties: extractProps(cssCustomProperties, 'defaultValue')
                    })
                }

                return _.defaultsDeep({}, ...defaultsFromPresets)
            }

            const getDefaultDefinition = (
                compPointer: CompRef,
                innerElementsPath: string[] = []
            ): Readonly<Pick<BuilderStyle, 'cssProperties' | 'cssCustomProperties'>> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const inflatedInnerElementsPath: string[] = inflateInnerElementsPath(innerElementsPath)
                const defaultsFromPresets = getDefaultAccordingToAppliedPresets(compPointer, innerElementsPath)
                const elementDefaults = {
                    cssProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssProperties'],
                            {}
                        ) satisfies CssProperties,
                        'defaultValue'
                    ),
                    cssCustomProperties: extractProps(
                        _.get(
                            editorElement,
                            [...inflatedInnerElementsPath, 'cssCustomProperties'],
                            {}
                        ) satisfies CssCustomProperties,
                        'defaultValue'
                    )
                }
                const {cssProperties, cssCustomProperties} = _.defaultsDeep(
                    defaultsFromPresets,
                    elementDefaults
                ) satisfies Pick<BuilderStyle, 'cssCustomProperties' | 'cssProperties'>

                return Object.freeze({
                    cssProperties: Object.freeze(cssProperties),
                    cssCustomProperties: Object.freeze(cssCustomProperties)
                })
            }

            const buildTraversableFlatMap = (compPointer: CompRef): Record<string, {innerElements: string[]}> => {
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
                const map: Record<string, {innerElements: string[]}> = {}

                const traverse = (current: Record<string, ElementItem> | ElementItem | undefined, path: string[]) => {
                    const currentPathKey = path.join('.')
                    map[currentPathKey] = {
                        innerElements: []
                    }

                    if (!current || typeof current !== 'object') {
                        return
                    }

                    for (const [key, value] of Object.entries(current)) {
                        const newPath = [...path, key]
                        const newPathKey = newPath.join('.')

                        map[currentPathKey].innerElements.push(newPathKey)

                        traverse(value?.inlineElement?.elements, newPath)
                    }
                }

                traverse(editorElement?.elements, [compPointer.id])
                return map
            }

            return {
                manifest: {
                    getArchType(componentType: string): Archetype {
                        const {editorElement} = getComponentDetailsFromCSM(componentType) ?? {}
                        return editorElement.archetype
                    },
                    getDisplayName(componentType: string) {
                        const {displayName} = getComponentDetailsFromCSM(componentType)?.editorElement ?? {}
                        return displayName
                    },
                    getStyle(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        return [
                            ...getComponentOverridesOrDefaultFromType(
                                compPointer,
                                {dataType: 'cssProperties', namespace: 'style'},
                                withInnerElements,
                                types
                            ),
                            ...getComponentOverridesOrDefaultFromType(
                                compPointer,
                                {dataType: 'cssCustomProperties', namespace: 'style'},
                                withInnerElements,
                                types
                            )
                        ]
                    },
                    getData(
                        compPointer: Pointer,
                        withInnerElements: boolean = false,
                        types?: {prop: string; extract?(data: any): any}[]
                    ) {
                        return getComponentOverridesOrDefaultFromType(
                            compPointer,
                            {dataType: 'data', namespace: 'data'},
                            withInnerElements,
                            types
                        )
                    },
                    cssStyle: {
                        getDefaultDefinition,
                        getCustomCssPropertiesRoles
                    },
                    innerElements: {
                        buildTraversableFlatMap
                    }
                }
            }
        }
    }
}

export {createExtension, ManifestApi, ManifestExtensionAPI}
